import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, signal, viewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { api } from '@consts/url.const';
import { routerSegments } from '@consts/routes';
import { IntelligenceModels } from '@models/intelligence.model';
import { ThumbOptions } from '../../../../../cameras/camera-thumbnails/camera-thumbnails.model';
import { ThumbnailTemplate } from '../../../../thumbnail/thumbnail.component';
import { SharedModule } from '../../../../shared.module';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { TranslatePipe } from '@ngx-translate/core';

const GRID_COLUMNS = 2;
const ROW_HEIGHT = 304;
const OVERSCAN_ROWS = 2;

@Component({
  selector: 'app-search-result',
  standalone: true,
  imports: [CommonModule, ScrollingModule, SharedModule, TranslatePipe],
  templateUrl: './search-result.component.html',
  styleUrls: ['./search-result.component.scss'],
})
export class SearchResultComponent {
  private readonly httpClient = inject(HttpClient);
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  public readonly viewport = viewChild(CdkVirtualScrollViewport);
  public readonly ThumbnailTemplate = ThumbnailTemplate;
  public readonly rowHeight = ROW_HEIGHT;
  public readonly minBufferPx = ROW_HEIGHT * OVERSCAN_ROWS;
  public readonly maxBufferPx = ROW_HEIGHT * (OVERSCAN_ROWS + 2);
  public readonly loadingMore = signal(false);
  public readonly appendedResults = signal<IntelligenceModels.Search.ResultItem[]>([]);
  public readonly more = signal(false);
  public readonly latestTs = signal<number | null>(null);

  public readonly response = computed<IntelligenceModels.Search.Response | null>(() => {
    const result = this.resultsService.current();
    if (result?.type !== 'search') return null;
    return result.data as IntelligenceModels.Search.Response;
  });

  public readonly results = computed(() => [...(this.response()?.results ?? []), ...this.appendedResults()]);
  public readonly rows = computed(() => {
    const items = this.results();
    return Array.from({ length: Math.ceil(items.length / GRID_COLUMNS) }, (_, index) =>
      items.slice(index * GRID_COLUMNS, index * GRID_COLUMNS + GRID_COLUMNS),
    );
  });

  constructor() {
    effect(() => {
      const response = this.response();
      this.appendedResults.set([]);
      this.more.set(response?.more ?? false);
      this.latestTs.set(response?.latestTs ?? null);
      queueMicrotask(() => this.viewport()?.scrollToIndex(0));
    }, { allowSignalWrites: true });
    this.resultsService.setHeaderActions([
      {
        icon: 'ui-icon-new-tab',
        tooltip: 'SEARCH_RESULT.OPEN_PAGE',
        onClick: () => this.openTextSearch(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  public onScrolledIndex(index: number): void {
    if (index >= this.rows().length - 2) void this.loadMore();
  }

  public thumbnailOptions(item: IntelligenceModels.Search.ResultItem): ThumbOptions {
    const startTime = item.trackTimeRange?.start ?? item.timestamp ?? 0;
    const endTime = item.trackTimeRange?.end ?? item.timestamp ?? startTime;
    return {
      clipInSeconds: Math.max((endTime - startTime) / 1000, 1),
      offsetResInDurations: 6,
      startTime,
      endTime,
      duration: Math.max(endTime - startTime, 1),
      cameraId: item.cameraId,
      edgeId: item.edgeId,
    };
  }

  private async loadMore(): Promise<void> {
    const response = this.response();
    const filter = response?.filter;
    const latestTs = this.latestTs();
    if (!this.more() || !latestTs || !filter || this.loadingMore()) return;
    this.loadingMore.set(true);
    try {
      let params = new HttpParams()
        .set('query', filter.query)
        .set('startTime', new Date(filter.startTime).toISOString())
        .set('endTime', new Date(filter.endTime).toISOString())
        .set('latestTs', String(latestTs))
        .set('size', String(filter.size));
      filter.cameraIds.forEach((cameraId) => {
        params = params.append('cameraIds', cameraId);
      });
      const next = await firstValueFrom(
        this.httpClient.get<IntelligenceModels.Search.Response>(`${api.intelligence.search}/smart`, { params }),
      );
      if (next.success && !next.needsInput) {
        this.appendedResults.update((items) => [...items, ...(next.results ?? [])]);
        this.more.set(next.more ?? false);
        this.latestTs.set(next.latestTs ?? null);
      }
    } finally {
      this.loadingMore.set(false);
    }
  }

  private openTextSearch(): void {
    const query = this.response()?.filter?.query ?? this.response()?.query ?? '';
    const tree = this.router.createUrlTree(['/', routerSegments.searchV2, routerSegments.textSearch], {
      queryParams: query ? { searchTerm: query } : {},
    });
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
